import { env } from '../env.ts';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  token?: string | null;
  idempotencyKey?: string;
  raw?: boolean;
  headers?: Record<string, string>;
}

export async function api<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    accept: 'application/json',
    ...(opts.headers ?? {}),
  };
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  if (opts.token) headers['authorization'] = `Bearer ${opts.token}`;
  if (opts.idempotencyKey) headers['idempotency-key'] = opts.idempotencyKey;

  const url = path.startsWith('http') ? path : `${env.apiBase}${path.replace(/^\/api/, '')}`;
  const res = await fetch(url, {
    method: opts.method ?? (opts.body ? 'POST' : 'GET'),
    headers,
    ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
  });
  if (opts.raw) return res as unknown as T;
  if (!res.ok) {
    let code = 'http_error';
    let message = `http_${res.status}`;
    try {
      const json = (await res.json()) as { error?: { code?: string; message?: string } };
      if (json.error?.code) code = json.error.code;
      if (json.error?.message) message = json.error.message;
    } catch {
      // ignore
    }
    throw new ApiError(res.status, code, message);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export interface ChallengeResponse {
  challengeId: string;
  prefix: string;
  difficulty: number;
  expiresAt: number;
}

export interface VerifyResponse {
  userId: string;
  sessionToken: string;
  ranked: boolean;
  expiresInMs: number;
}

export const apiAuth = {
  challenge: () => api<ChallengeResponse>('/auth/challenge'),
  verify: (input: { challengeId: string; nonce: string; accessJwt?: string }) =>
    api<VerifyResponse>('/auth/verify', { method: 'POST', body: input }),
  consent: (input: { biometricConsent: true; ageConfirm: true; tosVersion: number }, token: string) =>
    api<{ ok: true }>('/consent', { method: 'POST', body: input, token }),
};
